import { Text, Title } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { PromotionQuerySuggestionContainer } from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/PromotionQuerySuggestionContainer";
import { useIsAdminUser } from "@mirage/service-auth/useDropboxAccount";
import { useName } from "@mirage/service-auth/useName";
import { ConversationClientContext } from "@mirage/service-conversation/types";
import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import {
  LottieAnimation,
  LottieAnimationType,
} from "@mirage/shared/lottie-animations/LottieAnimation";
import { useIsSmallSizeForSidebar } from "@mirage/shared/responsive/mobile";
import i18n from "@mirage/translations";
import classNames from "classnames";
import React, { memo, useMemo } from "react";
import styles from "./EmptyChatInstructions.module.css";

interface ChatSidebarContent {
  title: React.ReactNode;
  subtitle: React.ReactNode;
  featuredImage?: React.ReactNode;
}

interface FSSEmptyChatInstructionsProps {
  isFirstTimeChat: boolean;
  clientContext?: ConversationClientContext;
  handlePostQuerySubmit: (hideFollowUpSuggestions: boolean) => void;
}

export const FSSEmptyChatInstructions: React.FC<FSSEmptyChatInstructionsProps> =
  memo(({ isFirstTimeChat, clientContext, handlePostQuerySubmit }) => {
    const { firstName } = useName();
    const isAdminUser = useIsAdminUser();

    const isSmallSize = useIsSmallSizeForSidebar();
    const isDesktop = EnvCtx.surface === "desktop";
    const isFSSChatFTUXEnabled = useFeatureFlagValueBool(
      "bridge_growth_2025_06_26_fss_chat_ftux",
      true,
    );

    const firstTimeChatGreetingImage = useMemo(() => {
      if (isFirstTimeChat) {
        return (
          <LottieAnimation
            animationType={LottieAnimationType.CHAT_GREETING}
            loop
            autoplay
            alt={i18n.t("greeting_image_dash_chat_alt_subtitle")}
            className={styles.GreetingImage}
          />
        );
      }
      return null;
    }, [isFirstTimeChat]);

    const getSidebarContent = (): ChatSidebarContent => {
      if (isFirstTimeChat) {
        const firstTimeChatContent = {
          title: i18n.t("greeting_first_time_dash_chat_fss"),
          subtitle: i18n.t("dash_chat_fss_first_time_subtitle"),
          featuredImage: firstTimeChatGreetingImage,
        };

        if (isAdminUser) {
          return {
            ...firstTimeChatContent,
            title: i18n.t("greeting_admin_first_time_dash_chat_fss"),
          };
        }

        return firstTimeChatContent;
      }

      return {
        title: i18n.t("greeting_casual_with_name", { name: firstName }),
        subtitle: i18n.t("assistant_empty_chat_subtitle"),
      };
    };

    const content = getSidebarContent();

    return (
      <div
        className={classNames(styles.EmptyChatInstructions, {
          [styles.Mobile]: isSmallSize,
          [styles.Desktop]: isDesktop,
        })}
      >
        <div
          className={classNames(styles.TitleContainer, {
            [styles.TitleContainerMobile]: isSmallSize,
          })}
        >
          <Box className={styles.TitleContainerBox}>
            <Title
              size="small"
              weightVariant="emphasized"
              className={styles.Title}
            >
              {content.title}
            </Title>
            <Text size="large" className={styles.Subtitle}>
              {content.subtitle}
            </Text>
          </Box>
          {content.featuredImage}
          {isFSSChatFTUXEnabled && (
            <PromotionQuerySuggestionContainer
              clientContext={clientContext}
              isFirstTimeChat={isFirstTimeChat}
              handlePostQuerySubmit={handlePostQuerySubmit}
            />
          )}
        </div>
      </div>
    );
  });

FSSEmptyChatInstructions.displayName = "FSSEmptyChatInstructions";
